import './resources'

import i18next from 'i18next'
import { initReactI18next } from 'react-i18next'

import b2gPack from './locales/b2g.json'
import creversePack from './locales/creverse.json'
import defaultPack from './locales/default.json'
import { type I18nBrand, resolveBrand } from './resolveBrand'

const LNG = 'custom'
const NS = 'translation'

// 모든 brand 번들은 default 와 동일한 키 집합을 가져야 한다 (없으면 컴파일 에러).
type BrandPack = Record<keyof typeof defaultPack, string>

const BRAND_PACKS: Record<I18nBrand, BrandPack> = {
  default: defaultPack,
  creverse: creversePack,
  b2g: b2gPack,
}

/**
 * 부팅 시 호출. default 번들을 base 로 깔아 빈 리소스 race 와 키 노출을 원천 차단한다.
 * brand 확정 전(로그인 페이지 등)에도 default 문구가 보장된다.
 */
export const initI18n = () =>
  i18next.use(initReactI18next).init({
    lng: LNG,
    react: { useSuspense: true },
    resources: { [LNG]: { [NS]: defaultPack } },
  })

/**
 * academy 확정 후 호출. brand 번들을 덮어쓴 뒤 remote 언어팩을 시도한다.
 * remote 성공 시 그 값으로 overwrite, 실패/timeout 시 brand 번들이 fallback 으로 남는다.
 *
 * brand 번들과 remote 는 같은 i18n 레포에서 나온 동일 출처라, remote 성공 시에도 깜빡임이 없다.
 */
export const bindI18nBrand = async (
  brandInput: Parameters<typeof resolveBrand>[0],
  languagePackUrl?: string | null,
) => {
  if (!i18next.isInitialized) return
  const brand = resolveBrand(brandInput)
  i18next.addResourceBundle(LNG, NS, BRAND_PACKS[brand], true, true)
  await i18next.changeLanguage(LNG)

  if (!languagePackUrl) return
  // CDN 지연으로 로그인 직후 진입이 블로킹되지 않도록 상한을 둔다.
  // timeout 시 fetch 가 abort 되어 이번 세션은 brand 번들 fallback 으로 동작한다.
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), 5000)
  try {
    const res = await fetch(languagePackUrl, { cache: 'no-cache', signal: controller.signal })
    if (!res.ok) return
    const resources = await res.json()
    i18next.addResourceBundle(LNG, NS, resources, true, true)
    await i18next.changeLanguage(LNG)
  } catch {
    // 실패/timeout 시 brand 번들 유지
  } finally {
    clearTimeout(timeoutId)
  }
}
